import React from "react";
import { Check, Server, X } from "lucide-react";
import {
  GetCloudName,
  GetProviderName,
  type GPUInstanceType,
} from "types/InstanceTypes";
import {
  formatMemoryForDisplay,
  isStoppableInstance,
} from "components/Environment/Create/utils";
import { displayPlatformType } from "components/Environment/utils";
import { formatSeconds, parseTimeToSeconds } from "utils/timeUtils";
import LaunchableComputeSelect from "../components/ComputeSelector";
import LocationSubLocationPicker from "../../LocationSubLocationPicker";
import { SectionShell } from "../layout";
import type { SectionControlProps } from "../types";

const HardwareCollapsedPreview: React.FC<{
  selectedInstanceType: GPUInstanceType;
  storage: string;
  location: string;
  subLocation: string;
}> = ({ selectedInstanceType, storage, location, subLocation }) => {
  const gpu = selectedInstanceType.supported_gpus?.[0];
  const estimatedStartSeconds = selectedInstanceType.estimated_deploy_time
    ? parseTimeToSeconds(selectedInstanceType.estimated_deploy_time)
    : undefined;
  const cloudName = GetCloudName(selectedInstanceType);
  const providerName = GetProviderName(selectedInstanceType);
  const cloud = providerName
    ? `${displayPlatformType(providerName)}${
        cloudName ? ` (${displayPlatformType(cloudName)})` : ""
      }`
    : displayPlatformType(cloudName || "Unknown");
  const primaryHardwareDetails = [
    gpu ? `${gpu.count || 1}× ${gpu.name}` : "No GPU",
    gpu ? `${formatMemoryForDisplay(gpu.memory)} VRAM` : "",
    `${selectedInstanceType.vcpu} CPUs`,
  ].filter(Boolean);
  const secondaryHardwareDetails = [
    `${formatMemoryForDisplay(selectedInstanceType.memory)} RAM`,
    `${storage} GiB storage`,
  ];
  const hasFlexiblePorts = selectedInstanceType.can_modify_firewall_rules;
  const hasStartStop = isStoppableInstance(selectedInstanceType.stoppable);

  return (
    <div className="mt-2 space-y-1 text-sm text-secondary">
      <div className="flex flex-wrap items-center gap-x-2">
        <span>{primaryHardwareDetails.join(" · ")}</span>
        <span className="hidden sm:inline" aria-hidden="true">
          ·
        </span>
        <span className="basis-full sm:basis-auto">
          {secondaryHardwareDetails.join(" · ")}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-x-2">
        <span>{cloud}</span>
        <span className="hidden sm:inline" aria-hidden="true">
          ·
        </span>
        <span className="basis-full sm:basis-auto">
          Instance type: {selectedInstanceType.type}
        </span>
        {location && (
          <>
            <span className="hidden sm:inline" aria-hidden="true">
              ·
            </span>
            <span className="basis-full sm:basis-auto">
              {location}
              {subLocation ? ` (${subLocation})` : ""}
            </span>
          </>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span>Capabilities:</span>
        <span className="inline-flex items-center gap-1">
          {hasFlexiblePorts ? (
            <Check className="h-3.5 w-3.5 text-highlight" />
          ) : (
            <X className="h-3.5 w-3.5 text-red-400" />
          )}
          Flexible Ports
        </span>
        <span className="inline-flex items-center gap-1">
          {hasStartStop ? (
            <Check className="h-3.5 w-3.5 text-highlight" />
          ) : (
            <X className="h-3.5 w-3.5 text-red-400" />
          )}
          Start/stop
        </span>
        <span className="hidden sm:inline" aria-hidden="true">
          ·
        </span>
        <span className="basis-full sm:basis-auto">
          {estimatedStartSeconds === undefined
            ? "Ready time unavailable"
            : `Ready in ${formatSeconds(estimatedStartSeconds)}`}
        </span>
      </div>
    </div>
  );
};

export const HardwareSection: React.FC<
  SectionControlProps & {
    selectedInstanceType: GPUInstanceType | undefined;
    onInstanceTypeChange: (instanceType: GPUInstanceType | undefined) => void;
    storage: string;
    onStorageChange: (storage: string) => void;
    location: string;
    onLocationChange: (location: string) => void;
    subLocation: string;
    onSubLocationChange: (subLocation: string) => void;
  }
> = ({
  selectedInstanceType,
  onInstanceTypeChange,
  storage,
  onStorageChange,
  location,
  onLocationChange,
  subLocation,
  onSubLocationChange,
  ...sectionProps
}) => (
  <SectionShell
    id="hardware"
    title="Default hardware configuration"
    summary={selectedInstanceType ? undefined : "Choose hardware"}
    collapsedPreview={
      selectedInstanceType ? (
        <HardwareCollapsedPreview
          selectedInstanceType={selectedInstanceType}
          storage={storage}
          location={location}
          subLocation={subLocation}
        />
      ) : undefined
    }
    icon={<Server className="h-5 w-5" />}
    {...sectionProps}
  >
    <div>
      <LaunchableComputeSelect
        selectedInstanceType={selectedInstanceType}
        setSelectedInstanceType={onInstanceTypeChange}
        setStorage={onStorageChange}
        storage={storage}
      />
      {selectedInstanceType && (
        <LocationSubLocationPicker
          instance={selectedInstanceType}
          location={location}
          onLocationChange={onLocationChange}
          subLocation={subLocation}
          onSubLocationChange={onSubLocationChange}
        />
      )}
    </div>
  </SectionShell>
);
